import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
    Plus,
    Trash2,
    Database,
    FileText,
    BookOpen,
    AlertCircle,
    X,
    Check,
    Search,
    Info,
    FilePlus
} from "lucide-react";
import { KnowledgeBase } from "@/api/tools/tools.types";
import { getAllFiles } from "@/api/files/files.api";
import type { FileDetails } from "@/api/files/files.types";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    Alert,
    AlertDescription,
} from "@/components/ui/alert";

interface KnowledgeBaseFormProps {
    value: KnowledgeBase[];
    onChange: (data: KnowledgeBase[]) => void;
    isReadOnly?: boolean;
}

export function KnowledgeBaseForm({ value, onChange, isReadOnly = false }: KnowledgeBaseFormProps) {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>(value || []);
    const [files, setFiles] = useState<FileDetails[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [error, setError] = useState<{ index: number | null; message: string | null }>({ index: null, message: null });
    const [editingBase, setEditingBase] = useState<KnowledgeBase & { isNew?: boolean }>({
        name: "",
        description: "",
        fileIds: [],
        provider: "google" as const
    });
    const [searchQuery, setSearchQuery] = useState("");

    // Memoize the update function
    const updateParent = useCallback((newData: KnowledgeBase[]) => {
        const isEqual = JSON.stringify(newData) === JSON.stringify(value);
        if (!isEqual) {
            onChange(newData);
        }
    }, [value, onChange]);

    // Fetch all files
    useEffect(() => {
        const fetchFiles = async () => {
            setIsLoading(true);
            try {
                const response = await getAllFiles();
                if (response.error) throw new Error(response.error);
                setFiles(response.data || []);
            } catch (err) {
                console.error("Failed to fetch files:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFiles();
    }, []);

    // Update local state when value prop changes
    useEffect(() => {
        const isEqual = JSON.stringify(knowledgeBases) === JSON.stringify(value);
        if (!isEqual) {
            setKnowledgeBases(value || []);
        }
    }, [value]);

    // Filter files based on search query
    const filteredFiles = searchQuery.trim() === "" 
        ? files 
        : files.filter(file => 
            file.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            file.id.toLowerCase().includes(searchQuery.toLowerCase())
          );

    // If query tool type is not selected, show a message similar to TransferForm
    if (!value) {
        return (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center h-[400px] w-full"
            >
                <div className="flex flex-col items-center justify-center text-center p-10 w-full max-w-2xl">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                        <BookOpen className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">QKnowledge Not Available</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                        This feature is only available for QKnowledge tools.
                    </p>
                </div>
            </motion.div>
        );
    }

    const validateKnowledgeBase = (kb: KnowledgeBase): string | null => {
        if (!kb.name.trim()) {
            return "Name is required";
        }
        if (!kb.description.trim()) {
            return "Description is required";
        }
        if (kb.fileIds.length === 0) {
            return "At least one file must be selected";
        }
        return null;
    };

    const handleAddNew = () => {
        const newBase = {
            name: "",
            description: "",
            fileIds: [],
            provider: "google" as const,
            isNew: true
        };
        setEditingBase(newBase);
        
        // Create a temporary entry
        const newId = `new-${Date.now()}`;
        
        // Add the temporary entry to the knowledgeBases array
        const newKnowledgeBases = [...knowledgeBases, newBase as KnowledgeBase];
        setKnowledgeBases(newKnowledgeBases);
        
        // Collapse all existing knowledge bases and open only the new one
        setExpandedItems([`item-${newKnowledgeBases.length - 1}`]);
    };

    const handleSave = (index: number) => {
        const kb = knowledgeBases[index];
        if (!kb) return;

        const validationError = validateKnowledgeBase(kb);
        if (validationError) {
            setError({ index, message: validationError });
            return;
        }

        // Clear "isNew" flag if it exists
        const updatedKb = { ...kb };
        if ('isNew' in updatedKb) {
            delete (updatedKb as any).isNew;
        }

        const newKnowledgeBases = [...knowledgeBases];
        newKnowledgeBases[index] = updatedKb;
        
        setKnowledgeBases(newKnowledgeBases);
        updateParent(newKnowledgeBases);
        setError({ index: null, message: null });
        
        // Close the accordion
        setExpandedItems(expandedItems.filter(id => id !== `item-${index}`));
    };

    const handleEdit = (index: number) => {
        // Open the accordion item when editing
        if (!expandedItems.includes(`item-${index}`)) {
            setExpandedItems([...expandedItems, `item-${index}`]);
        }
    };

    const handleCancel = (index: number) => {
        const kb = knowledgeBases[index];
        
        // If it's a new item that hasn't been saved yet, remove it
        if (kb && (kb as any).isNew) {
            const newKnowledgeBases = knowledgeBases.filter((_, i) => i !== index);
            setKnowledgeBases(newKnowledgeBases);
            updateParent(newKnowledgeBases);
        }
        
        // Close the accordion
        setExpandedItems(expandedItems.filter(id => id !== `item-${index}`));
        setError({ index: null, message: null });
    };

    const handleDelete = (index: number) => {
        const newKnowledgeBases = knowledgeBases.filter((_, i) => i !== index);
        setKnowledgeBases(newKnowledgeBases);
        updateParent(newKnowledgeBases);
        setError({ index: null, message: null });
    };

    const updateKnowledgeBase = (index: number, updates: Partial<KnowledgeBase>) => {
        const kb = knowledgeBases[index];
        if (!kb) return;
        
        const newKnowledgeBases = [...knowledgeBases];
        newKnowledgeBases[index] = { ...kb, ...updates };
        setKnowledgeBases(newKnowledgeBases);
    };

    const handleToggleFile = (fileId: string, index: number) => {
        const kb = knowledgeBases[index];
        if (!kb) return;
        
        const fileIds = [...kb.fileIds];
        const fileIndex = fileIds.indexOf(fileId);
        
        if (fileIndex === -1) {
            fileIds.push(fileId);
        } else {
            fileIds.splice(fileIndex, 1);
        }
        
        updateKnowledgeBase(index, { fileIds });
    };

    const handleRemoveFile = (fileId: string, index: number) => {
        const kb = knowledgeBases[index];
        if (!kb) return;
        
        const fileIds = kb.fileIds.filter(id => id !== fileId);
        updateKnowledgeBase(index, { fileIds });
    };

    const getFileNameById = (fileId: string) => {
        const file = files.find(f => f.id === fileId);
        return file ? file.name : fileId;
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            {/* QKnowledge Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Database className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>QKnowledge Bases</CardTitle>
                            <CardDescription>
                                Configure knowledge bases to use with your query tool
                            </CardDescription>
                        </div>
                        {knowledgeBases.length > 0 && !isReadOnly && (
                            <Button
                                size="sm"
                                onClick={handleAddNew}
                                className="gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add Knowledge Base
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {knowledgeBases.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <Database className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-sm font-medium mb-1">No Knowledge Bases</h3>
                            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                Create your first knowledge base to start querying files
                            </p>
                            {!isReadOnly && (
                                <Button
                                    size="sm"
                                    onClick={handleAddNew}
                                    className="gap-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Your First Knowledge Base
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Accordion
                            type="multiple"
                            value={expandedItems}
                            onValueChange={setExpandedItems}
                            className="space-y-3"
                        >
                            {knowledgeBases.map((kb, index) => (
                                <AccordionItem
                                    key={`kb-${index}`}
                                    value={`item-${index}`}
                                    className="border rounded-md overflow-hidden"
                                >
                                    <AccordionTrigger className="px-4 py-3 hover:bg-muted/50 data-[state=open]:bg-muted/80">
                                        <div className="flex items-center justify-between w-full">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-primary/10 p-1.5 rounded-md">
                                                    <BookOpen className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="text-left">
                                                    <h4 className="text-sm font-medium">
                                                        {kb.name || `Knowledge Base ${index + 1}`}
                                                    </h4>
                                                    {kb.fileIds.length > 0 && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {kb.fileIds.length} file{kb.fileIds.length !== 1 ? 's' : ''}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!isReadOnly && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 mr-2 hover:bg-destructive/10 hover:text-destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(index);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="bg-muted/20 border-t">
                                        <div className="p-4 space-y-6">
                                            {error.index === index && error.message && (
                                                <Alert variant="destructive">
                                                    <AlertCircle className="h-4 w-4" />
                                                    <AlertDescription>{error.message}</AlertDescription>
                                                </Alert>
                                            )}

                                            {/* Basic Information Card */}
                                            <Card className="shadow-sm">
                                                <CardHeader className="border-b bg-muted/20 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                            <Info className="h-3.5 w-3.5 text-primary" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-sm font-medium leading-none">Basic Information</h4>
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    <div className="space-y-2">
                                                        <Label 
                                                            htmlFor={`kb-name-${index}`}
                                                            className="flex items-center gap-2 text-sm font-medium"
                                                        >
                                                            <FileText className="h-4 w-4 text-primary" />
                                                            Name
                                                        </Label>
                                                        <Input
                                                            id={`kb-name-${index}`}
                                                            className="h-9"
                                                            value={kb.name}
                                                            onChange={(e) => updateKnowledgeBase(index, { name: e.target.value })}
                                                            placeholder="e.g., Product Documentation, Customer FAQs"
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label 
                                                            htmlFor={`kb-description-${index}`}
                                                            className="flex items-center gap-2 text-sm font-medium"
                                                        >
                                                            <Info className="h-4 w-4 text-primary" />
                                                            Description
                                                        </Label>
                                                        <Textarea
                                                            id={`kb-description-${index}`}
                                                            className="min-h-[100px] resize-none"
                                                            value={kb.description}
                                                            onChange={(e) => updateKnowledgeBase(index, { description: e.target.value })}
                                                            placeholder="Describe what this knowledge base contains and when it should be used..."
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            {/* Files Card */}
                                            <Card className="shadow-sm">
                                                <CardHeader className="border-b bg-muted/20 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                            <FilePlus className="h-3.5 w-3.5 text-primary" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-sm font-medium leading-none">Files</h4>
                                                        </div>
                                                        {!isReadOnly && (
                                                            <Popover>
                                                                <PopoverTrigger asChild>
                                                                    <Button 
                                                                        variant="outline" 
                                                                        size="sm"
                                                                        className="gap-2"
                                                                    >
                                                                        <Plus className="h-3.5 w-3.5" />
                                                                        Add Files
                                                                    </Button>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-[400px] p-0" align="end">
                                                                    <div className="px-4 py-3 border-b bg-muted/20">
                                                                        <h4 className="text-sm font-medium mb-2">Select Files</h4>
                                                                        <div className="relative">
                                                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                                            <Input
                                                                                placeholder="Search by name or ID..."
                                                                                className="h-9 pl-9"
                                                                                value={searchQuery}
                                                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <ScrollArea className="h-[300px]">
                                                                        {isLoading ? (
                                                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                                                Loading files...
                                                                            </div>
                                                                        ) : files.length === 0 ? (
                                                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                                                No files available
                                                                            </div>
                                                                        ) : filteredFiles.length === 0 ? (
                                                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                                                No files match your search
                                                                            </div>
                                                                        ) : (
                                                                            <div className="p-2">
                                                                                {filteredFiles.map(file => {
                                                                                    const isSelected = kb.fileIds.includes(file.id);
                                                                                    return (
                                                                                        <div
                                                                                            key={file.id}
                                                                                            className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-muted' : ''}`}
                                                                                            onClick={() => handleToggleFile(file.id, index)}
                                                                                        >
                                                                                            <div className={`flex-shrink-0 w-4 h-4 rounded-sm border ${isSelected ? 'bg-primary border-primary' : 'border-input'} flex items-center justify-center`}>
                                                                                                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                                                                                            </div>
                                                                                            <div className="flex-1 min-w-0">
                                                                                                <div className="text-sm font-medium truncate">{file.name}</div>
                                                                                                <div className="text-xs text-muted-foreground truncate">{file.id}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </ScrollArea>
                                                                </PopoverContent>
                                                            </Popover>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    {kb.fileIds.length === 0 ? (
                                                        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded-md">
                                                            No files selected
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2">
                                                            {kb.fileIds.map(fileId => (
                                                                <div
                                                                    key={fileId}
                                                                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border text-sm"
                                                                >
                                                                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                                    <span className="max-w-[200px] truncate">{getFileNameById(fileId)}</span>
                                                                    {!isReadOnly && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-5 w-5 p-0 hover:bg-background"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveFile(fileId, index);
                                                                            }}
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </CardContent>
                                                {!isReadOnly && (
                                                    <div className="flex justify-end gap-2 px-6 py-3 border-t bg-muted/20">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleCancel(index)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleSave(index)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
}
